import React, { useMemo, useState } from "react";
import { formatEdition, formatExpiresInDays } from "../utils/licenceUtils";
import { applyPartnerDiscount } from "../utils/partnerPricing";

const RENEW_YEAR_OPTIONS = [1, 2, 3, 4, 5];
const SIMULTANEOUS_CALL_OPTIONS = [
  { sc: 8, maxExtensions: 40 },
  { sc: 16, maxExtensions: 80 },
  { sc: 24, maxExtensions: 120 },
  { sc: 32, maxExtensions: 192 },
  { sc: 48, maxExtensions: 288 },
  { sc: 64, maxExtensions: 384 },
  { sc: 96, maxExtensions: 672 },
  { sc: 128, maxExtensions: 896 },
  { sc: 192, maxExtensions: 1344 },
  { sc: 256, maxExtensions: 2048 },
];

function getAvailableEditionOptions(currentEdition) {
  // Always allow selecting any edition.
  // We keep internal values (Basic/Professional/Enterprise) and format labels via formatEdition().
  return ["Basic", "Professional", "Enterprise"];
}

function getDisplayType(licence) {
  if (!licence) return "-";
  if (licence.IsPerpetual) return "Perpetual";
  if (licence.Type === "Commercial") return "Annual";
  return licence.Type || "-";
}

function formatMoney(value, currency = "EUR") {
  if (value == null || Number.isNaN(Number(value))) return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function renderPriceError(message) {
  const msg = String(message || "").trim();
  if (!msg) return null;

  const match = msg.match(/^(.*?)(\bhere)\s+(https?:\/\/\S+)(.*)$/i);
  if (!match) return msg;

  const [, prefix, hereWord, url, suffix] = match;
  return (
    <>
      {prefix}
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="underline underline-offset-2"
      >
        {hereWord}
      </a>
      {suffix}
    </>
  );
}

function WarningModal({ open, onClose, onConfirm, submitting }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[rgba(61,57,53,0.24)] px-4 py-6 backdrop-blur-[2px]">
      <div className="w-full max-w-[460px] overflow-hidden rounded-[12px] border border-[#facc15] bg-[#ececeb] shadow-[0_20px_60px_rgba(250,204,21,0.18)]">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full border border-[#facc15] bg-[rgba(250,204,21,0.18)]">
              <span className="text-[22px] font-semibold leading-none text-[#facc15]">
                !
              </span>
            </div>

            <div className="flex-1">
              <h3 className="text-[20px] font-semibold text-[#3D3935]">
                Warning
              </h3>

              <p className="mt-3 text-[15px] leading-7 text-[#465064]">
                Please review the selected renewal options carefully before
                creating the order.
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-[10px] border border-[#3D3935] bg-transparent px-5 py-2.5 text-[14px] font-medium text-[#3D3935] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              className="rounded-[10px] border border-[#facc15] bg-white px-5 py-2.5 text-[14px] font-medium text-[#3D3935] transition hover:bg-[#facc15] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Processing..." : "Create order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function pickNumber(...values) {
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function getComputedSummary(
  priceResult,
  selectedEdition,
  years,
  addHosting,
  licenseDiscountPercent,
  hostingDiscountPercent,
) {
  if (!priceResult) return null;

  const currency = priceResult.Currency || "EUR";
  const selectedYears = Number(priceResult.Years ?? years ?? 1) || 1;

  const yearLicenseRetail = pickNumber(
    priceResult.YearPriceWithoutHosting,
    priceResult.PriceWithoutHosting,
  );
  const yearHostingRetail = pickNumber(
    priceResult.YearHostingPrice,
    priceResult.HostingPrice,
  );

  const licenseRetail = yearLicenseRetail * selectedYears;
  const hostingRetail = addHosting ? yearHostingRetail * selectedYears : 0;

  const appliedLicenseDiscount = Math.min(
    100,
    Math.max(0, Number(licenseDiscountPercent) || 0),
  );
  const appliedHostingDiscount = Math.min(
    100,
    Math.max(0, Number(hostingDiscountPercent) || 0),
  );

  const licensePrice = applyPartnerDiscount(licenseRetail, appliedLicenseDiscount);
  const hostingPrice = addHosting
    ? applyPartnerDiscount(hostingRetail, appliedHostingDiscount)
    : 0;

  const totalPrice = addHosting ? licensePrice + hostingPrice : licensePrice;

  return {
    currency,
    maxExtensions: priceResult.ToMaxExtensions ?? priceResult.MaxExtensions ?? "-",
    simultaneousCalls:
      priceResult.ToSimultaneousCalls ?? priceResult.SimultaneousCalls ?? "-",
    edition: formatEdition(priceResult.Edition ?? selectedEdition),
    years: selectedYears,
    licenseRetail,
    hostingRetail,
    licensePrice,
    hostingPrice,
    licenseDiscountPercent: appliedLicenseDiscount,
    hostingDiscountPercent: appliedHostingDiscount,
    totalPrice,
  };
}

export default function RenewModal({
  open,
  licence,
  years,
  onYearsChange,
  simultaneousCalls,
  onSimultaneousCallsChange,
  selectedEdition,
  onEditionChange,
  addHosting,
  onAddHostingChange,
  hostingLocked = false,
  keyInfoLoading = false,
  licenseDiscountPercent = 0,
  hostingDiscountPercent = 0,
  onClose,
  onSubmit,
  submitting,
  priceResult,
  priceLoading,
  priceError,
}) {
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const editionOptions = useMemo(() => {
    return getAvailableEditionOptions(licence?.Edition);
  }, [licence]);

  const computedSummary = useMemo(() => {
    return getComputedSummary(
      priceResult,
      selectedEdition,
      years,
      addHosting,
      licenseDiscountPercent,
      hostingDiscountPercent,
    );
  }, [
    priceResult,
    selectedEdition,
    years,
    addHosting,
    licenseDiscountPercent,
    hostingDiscountPercent,
  ]);

  if (!open || !licence) return null;

  const handlePrimaryAction = () => {
    if (submitting || !computedSummary) return;
    setIsWarningOpen(true);
  };

  const handleCloseWarning = () => {
    if (submitting) return;
    setIsWarningOpen(false);
  };

  const handleConfirmWarning = async () => {
    await onSubmit();
    setIsWarningOpen(false);
  };

  const handleCloseMainModal = () => {
    if (isWarningOpen || submitting) return;
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(61,57,53,0.14)] px-4 py-6 backdrop-blur-[2px]">
        <div className="relative w-full max-w-6xl overflow-hidden rounded-[12px] border border-[#3D3935] bg-[#ececeb] shadow-[0_20px_60px_rgba(61,57,53,0.12)]">
          {keyInfoLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[12px] bg-[rgba(236,236,235,0.85)] backdrop-blur-[1px]">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-[#ffbf3c]" />
            </div>
          )}

          <button
            type="button"
            onClick={handleCloseMainModal}
            className="absolute right-5 top-4 z-20 text-[34px] leading-none text-[#3D3935] transition hover:opacity-65"
            aria-label="Close"
          >
            ×
          </button>

          <div className="grid grid-cols-1 items-stretch gap-8 p-6 md:grid-cols-[1.65fr_0.87fr] md:p-8">
            <div>
              <h2 className="text-[20px] font-semibold text-[#3D3935]">
                3CX Shopping Cart
              </h2>

              <p className="mt-3 text-[17px] text-[#3D3935]">
                Renewal for {licence.LicenseKey}
              </p>

              <div className="mt-7 grid grid-cols-[180px_1fr] items-start gap-x-6">
                <div className="pt-[2px] text-[15px] font-semibold uppercase tracking-[0.02em] text-[#3D3935]">
                  Subscription Details
                </div>

                <div className="grid grid-cols-[230px_1fr] gap-y-2 text-[14px] leading-[1.35] text-[#465064]">
                  <div className="font-semibold text-[#3D3935]">
                    3CX Version:
                  </div>
                  <div>20.0</div>

                  <div className="font-semibold text-[#3D3935]">Type:</div>
                  <div>{getDisplayType(licence)}</div>

                  <div className="font-semibold text-[#3D3935]">Edition:</div>
                  <div>{formatEdition(selectedEdition)}</div>

                  <div className="font-semibold text-[#3D3935]">
                    Simultaneous Calls:
                  </div>
                  <div>{licence.SimultaneousCalls || "-"}</div>

                  <div className="font-semibold text-[#3D3935]">
                    Subscription expires in:
                  </div>
                  <div>{formatExpiresInDays(licence.ExpiryDate)}</div>

                  <div className="font-semibold text-[#3D3935]">
                    Hosting expires in:
                  </div>
                  <div>{formatExpiresInDays(licence.ExpiryDate)}</div>

                  <div className="font-semibold text-[#3D3935]">
                    Maximum Number of Extensions:
                  </div>
                  <div>{computedSummary?.maxExtensions ?? "-"}</div>
                </div>
              </div>

              <div className="mt-12 space-y-10">
                <div className="grid grid-cols-[130px_1fr] items-center gap-5">
                  <label
                    htmlFor="renew-edition"
                    className="text-[14px] font-semibold text-[#3D3935]"
                  >
                    Edition
                  </label>

                  <div className="relative">
                    <select
                      id="renew-edition"
                      value={selectedEdition}
                      onChange={(e) => onEditionChange(e.target.value)}
                      className="w-full appearance-none border-0 border-b border-[#3D3935] bg-transparent pb-2 pr-8 text-[16px] text-[#465064] outline-none"
                    >
                      {editionOptions.map((option) => (
                        <option key={option} value={option}>
                          {formatEdition(option)}
                        </option>
                      ))}
                    </select>

                    <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[#3D3935]">
                      ▾
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-[130px_1fr] items-center gap-5">
                  <label
                    htmlFor="renew-simcalls"
                    className="text-[14px] font-semibold text-[#3D3935]"
                  >
                    Number of
                    <br />
                    Simultaneous Calls
                  </label>

                  <div className="relative">
                    <select
                      id="renew-simcalls"
                      value={simultaneousCalls}
                      onChange={(e) => onSimultaneousCallsChange(Number(e.target.value))}
                      className="w-full appearance-none border-0 border-b border-[#3D3935] bg-transparent pb-2 pr-8 text-[16px] text-[#465064] outline-none"
                    >
                      {SIMULTANEOUS_CALL_OPTIONS.map((option) => (
                        <option key={option.sc} value={option.sc}>
                          {option.sc} (max {option.maxExtensions} extensions)
                        </option>
                      ))}
                    </select>

                    <span className="pointer-events-none absolute right-1 top-[8px] text-[#3D3935]">
                      ▾
                    </span>
                    <div className="pt-2 text-[12px] text-[#6b6b6b]">
                      Specify the number of calls your system (internal and external)
                      will need to handle simultaneously.
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-[130px_1fr] items-center gap-5">
                  <label
                    htmlFor="renew-years"
                    className="text-[14px] font-semibold text-[#3D3935]"
                  >
                    Renew for
                  </label>

                  <div className="relative">
                    <select
                      id="renew-years"
                      value={years}
                      onChange={(e) => onYearsChange(Number(e.target.value))}
                      className="w-full appearance-none border-0 border-b border-[#3D3935] bg-transparent pb-2 pr-20 text-[16px] text-[#465064] outline-none"
                    >
                      {RENEW_YEAR_OPTIONS.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>

                    <span className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 text-[14px] text-[#465064]">
                      Years
                    </span>
                    <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[#3D3935]">
                      ▾
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-[130px_1fr] items-start gap-5">
                  <label
                    htmlFor="hosting-checkbox"
                    className="text-[14px] font-semibold leading-[20px] text-[#3D3935]"
                  >
                    Add Hosting by
                    <br />
                    3CX
                  </label>

                  <label
                    htmlFor={hostingLocked ? undefined : "hosting-checkbox"}
                    className={`flex select-none items-center gap-3 ${hostingLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  >
                    <input
                      id="hosting-checkbox"
                      type="checkbox"
                      checked={addHosting}
                      disabled={hostingLocked}
                      onChange={(e) => onAddHostingChange(e.target.checked)}
                      className="sr-only"
                    />

                    <span
                      className={`relative flex h-[20px] w-[20px] items-center justify-center rounded-[4px] border border-[#3D3935] transition ${
                        addHosting ? "bg-[#3D3935]" : "bg-transparent"
                      }`}
                    >
                      <span
                        className={`absolute left-1/2 top-1/2 h-[9px] w-[5px] -translate-x-1/2 -translate-y-[58%] rotate-45 border-b-[2px] border-r-[2px] border-[#ffbf3c] transition ${
                          addHosting ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex h-full flex-col border-t border-[#3D3935] pt-6 md:border-l md:border-t-0 md:pl-8 md:pt-0">
              <h3 className="text-[22px] font-semibold text-[#3D3935]">
                Price Summary
              </h3>

              <div className="mt-6 space-y-4 text-[15px] text-[#465064]">
                {priceLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-100 border-t-[#ffbf3c]" />
                  </div>
                ) : priceError ? (
                  <div className="rounded-[10px] border border-[#facc15] bg-[rgba(250,204,21,0.18)] px-4 py-3 text-sm text-[#3D3935]">
                    {renderPriceError(priceError)}
                  </div>
                ) : !computedSummary ? (
                  <div className="text-sm text-red-600">
                    No price found for selected options.
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4 border-b border-[#3D3935] pb-3">
                      <div className="max-w-[220px] leading-6">
                        {computedSummary.edition}{" "}
                        {computedSummary.simultaneousCalls}SC{" "}
                        {computedSummary.years} Year
                        {computedSummary.years > 1 ? "s" : ""}
                      </div>
                      <div className="whitespace-nowrap text-[#3D3935]">
                        {formatMoney(
                          computedSummary.licensePrice,
                          computedSummary.currency,
                        )}
                      </div>
                    </div>

                    {addHosting && computedSummary.hostingPrice > 0 && (
                      <div className="flex items-start justify-between gap-4 border-b border-[#3D3935] pb-3">
                        <div className="max-w-[220px] leading-6">
                          Hosting by 3CX
                        </div>
                        <div className="whitespace-nowrap text-[#3D3935]">
                          {formatMoney(
                            computedSummary.hostingPrice,
                            computedSummary.currency,
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-start justify-between gap-4 border-b border-[#3D3935] pb-3">
                      <div>Quantity</div>
                      <div className="text-[#3D3935]">1</div>
                    </div>

                    <div className="flex items-start justify-between gap-4 pt-1 text-[20px] font-semibold text-[#3D3935]">
                      <div>Total</div>
                      <div className="whitespace-nowrap">
                        {formatMoney(
                          computedSummary.totalPrice,
                          computedSummary.currency,
                        )}
                      </div>
                    </div>

                    <p className="pt-3 text-[13px] italic text-[#6b6b6b]">
                      *These prices do not include any taxes or VAT.
                    </p>
                  </>
                )}
              </div>

              <div className="mt-auto flex justify-end pt-8">
                <button
                  type="button"
                  onClick={handlePrimaryAction}
                  disabled={submitting || !computedSummary || priceLoading}
                  className="rounded-[10px] bg-white px-6 py-3 text-[14px] font-medium text-[#3D3935] transition hover:bg-[#ffbf3c] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Processing..." : "Create order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WarningModal
        open={isWarningOpen}
        onClose={handleCloseWarning}
        onConfirm={handleConfirmWarning}
        submitting={submitting}
      />
    </>
  );
}
